/**
 * Persistence contract. See implementation-plan.md §4.
 *
 * The target browser is Firefox, which has no File System Access API: every
 * save is a download, and the .json file on disk is the only copy of the user's
 * work. `canSaveInPlace` exists so a Chromium implementation can be added later
 * as progressive enhancement without the rest of the app changing.
 */

import { migrate } from '../model/migrate';
import type { ChronoDocument } from '../model/types';

/** Stable name for the working file: Ctrl+S always writes this one. */
export const WORKING_FILENAME = 'chrono-arrow.json';

/**
 * Dated name for "Save a copy" and for the standalone export: history is built
 * on demand, not accumulated.
 */
export function copyFilename(now: Date = new Date(), extension = 'json'): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return `chrono-arrow-${date}-${pad(now.getHours())}${pad(now.getMinutes())}.${extension}`;
}

export type OpenResult =
  | { kind: 'ok'; doc: ChronoDocument }
  | { kind: 'cancelled' }
  | { kind: 'invalid'; errors: string[] };

export interface DocumentStore {
  readonly canSaveInPlace: boolean;
  open(): Promise<OpenResult>;
  save(doc: ChronoDocument, filename: string): Promise<void>;
}

/**
 * Shared read path for both the file picker and drag-and-drop, so a dropped
 * file and a browsed one fail in exactly the same way.
 */
export async function readDocumentFile(file: File): Promise<OpenResult> {
  let text: string;
  try {
    text = await file.text();
  } catch {
    return { kind: 'invalid', errors: [`Impossible de lire « ${file.name} ».`] };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    return { kind: 'invalid', errors: [`« ${file.name} » n'est pas du JSON valide : ${detail}`] };
  }

  const result = migrate(parsed);
  return result.ok ? { kind: 'ok', doc: result.doc } : { kind: 'invalid', errors: result.errors };
}
